import { useState, type CSSProperties } from "react";
import { tokens } from "@/art/tokens";
import { paletteFor, RARITY_PALETTE } from "@/utils/stats";
import type { ItemRarity, RarityDisplay } from "@/api/types";
import { cn } from "@/utils/cn";

/**
 * 소형 토큰 스프라이트 — 영혼·운명·시나리오·아이템 카드의 **압축 표상**.
 * 기본 64×64, Umamusume 식 "트레이닝 버튼에 배치된 카드 아이콘" 같은 맥락에서 사용.
 *
 * 경로 규약 — 엔터티별 per-id 폴더 구조로 통일:
 *   /art/characters/{id}/face.png   (450px 원형 크롭)
 *   /art/destinies/{id}/portrait.png
 *   /art/scenarios/{id}/portrait.png
 *   /art/tokens/items/{itemId}.png  (아이템은 단일 파일 유지)
 *
 * 이미지 없으면 sigil + 이니셜 폴백. 레어도 팔레트 테두리 자동.
 */

export type TokenKind = "character" | "destiny" | "scenario" | "item";

interface TokenSpriteProps {
    kind: TokenKind;
    id: string;
    /** 표시할 이니셜 또는 폴백 짧은 텍스트. */
    fallbackLabel?: string;
    /** 레어도 팔레트 (장비/운명 등에만 적용). */
    rarity?: RarityDisplay | ItemRarity | string | null;
    /** 픽셀. 기본 64. */
    size?: number;
    /** 선택·강조 표시 (ember 글로우). */
    highlighted?: boolean;
    /** 디밍 (예: 비활성 슬롯). */
    dimmed?: boolean;
    className?: string;
    style?: CSSProperties;
    onClick?: () => void;
    /** 아래에 겹치는 작은 배지 텍스트 (예: 친밀도 "★★★"). */
    badge?: string;
}

const KIND_PATHS: Record<TokenKind, string> = {
    character: "/art/tokens/characters",
    destiny: "/art/tokens/destinies",
    scenario: "/art/tokens/scenarios",
    item: "/art/tokens/items",
};

/**
 * 전용 토큰이 없는 종류는 per-id 폴더의 portrait/face 로 폴백.
 * character: /art/characters/{id}/face.png (450px 원형 크롭)
 * destiny:   /art/destinies/{id}/portrait.png
 * scenario:  /art/scenarios/{id}/portrait.png
 */
function kindFallbackPath(kind: TokenKind, id: string): string | null {
    switch (kind) {
        case "character": return `/art/characters/${id}/face.png`;
        case "destiny":   return `/art/destinies/${id}/portrait.png`;
        case "scenario":  return `/art/scenarios/${id}/portrait.png`;
        default: return null;
    }
}

const KIND_SIGIL: Record<TokenKind, string> = {
    character: "/art/icons/soul-card-sigil.png",
    destiny: "/art/icons/destiny-pack-sigil.png",
    scenario: "/art/icons/tale-card-sigil.png",
    item: "/art/icons/splash-sigil.png",
};

const AVAILABLE_DESTINY_PORTRAITS = new Set([
    "abandoned-oracle",
    "blackmarket-ally",
    "borrowed-face",
    "crooked-dice",
    "eclipse-patron",
    "fallen-bloodline",
    "glassblower-hand",
    "golden-smile",
    "ghost-audience",
    "hearthside-inn",
    "late-quartermaster",
    "last-retainer",
    "letter-from-home",
    "moonlit-scholar",
    "nameless-wind",
    "nightmare-ledger",
    "pocket-saint",
    "reverse-compass",
    "rusty-cogwheel",
    "second-harvest",
    "stormglass-engine",
    "three-calamities",
    "tomorrow-auction",
    "traveling-apothecary",
    "unknown-sword",
    "wolf-at-window",
    "windless-step",
    "ashen-vow",
    "lantern-beneath-ribs",
    "borrowed-throne",
    "map-of-falling-stars",
    "thorn-crown",
    "tarot-of-nine",
    "steady-anvil",
    "festival-of-masks",
]);

export function TokenSprite({
    kind,
    id,
    fallbackLabel,
    rarity,
    size = 64,
    highlighted = false,
    dimmed = false,
    className,
    style,
    onClick,
    badge,
}: TokenSpriteProps) {
    // 레어도 팔레트: 객체/문자열/null 모두 처리
    const palette = typeof rarity === "string" && rarity in RARITY_PALETTE
        ? RARITY_PALETTE[rarity]
        : paletteFor(rarity as RarityDisplay | ItemRarity | null | undefined);

    const borderColor = highlighted ? tokens.color.ember : palette.border;
    const glow = highlighted ? `0 0 20px ${tokens.color.ember}AA`
        : `0 0 12px ${palette.glow}`;

    // 1차: tokens/{kind}/{id}.png — 실패하면 2차: kindFallbackPath(kind,id) — 그것도 실패면 sigil 폴백.
    // destiny 는 tokens/destinies/ 가 비어 있어 항상 portrait 로 해석됨.
    // character 는 face.png(450px 원형 크롭) 로 폴백 — 토큰 UI 에 최적.
    const primarySrc = `${KIND_PATHS[kind]}/${id}.png`;
    const fallbackSrc = kindFallbackPath(kind, id);
    const hasKnownFallback =
        kind === "destiny"
            ? AVAILABLE_DESTINY_PORTRAITS.has(id)
            : Boolean(fallbackSrc);
    const [srcStage, setSrcStage] = useState<"primary" | "fallback" | "sigil">(
        // destiny 는 존재하는 portrait 만 요청하고, 나머지는 바로 sigil 로 내려 404 를 막는다.
        kind === "destiny" ? (hasKnownFallback ? "fallback" : "sigil") : "primary",
    );
    const currentSrc =
        srcStage === "primary" ? primarySrc :
        srcStage === "fallback" && hasKnownFallback ? fallbackSrc :
        null;
    const sigilSrc = KIND_SIGIL[kind];
    const initial = (fallbackLabel ?? id.charAt(0)).toUpperCase();

    return (
        <button
            type="button"
            disabled={!onClick}
            onClick={onClick}
            className={cn(
                "relative rounded-pill flex items-center justify-center overflow-hidden transition-transform",
                onClick && "cursor-pointer hover:scale-[1.04] active:scale-[0.96]",
                className,
            )}
            style={{
                width: size,
                height: size,
                background: tokens.color.ink,
                border: `2px solid ${borderColor}`,
                boxShadow: glow,
                opacity: dimmed ? 0.45 : 1,
                ...style,
            }}
            aria-label={`${kind} token ${id}`}
        >
            {/* 기본 스프라이트 — primary → fallback → sigil 순 탐색. */}
            {currentSrc && (
                <img
                    src={currentSrc}
                    alt=""
                    onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                        if (srcStage === "primary" && hasKnownFallback) setSrcStage("fallback");
                        else setSrcStage("sigil");
                    }}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            )}
            {/* 폴백 레이어 — sigil + 이니셜 */}
            <div
                className="absolute inset-0 flex items-center justify-center"
                style={{
                    background: `radial-gradient(circle, ${palette.fillSoft} 0%, ${tokens.color.ink} 80%)`,
                }}
            >
                <img
                    src={sigilSrc}
                    alt=""
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    className="absolute h-3/5 w-3/5 object-contain"
                    style={{ opacity: 0.35 }}
                />
                <span
                    className="relative font-display select-none"
                    style={{
                        color: palette.text,
                        fontSize: size * 0.42,
                        letterSpacing: tokens.type.letterSpacing.tight,
                    }}
                >
                    {initial}
                </span>
            </div>

            {badge && (
                <span
                    className="absolute bottom-0 left-0 right-0 font-stat text-center"
                    style={{
                        fontSize: Math.max(9, size * 0.18),
                        color: tokens.color.ember,
                        background: `${tokens.color.ink}DD`,
                        padding: "1px 2px",
                        letterSpacing: tokens.type.letterSpacing.wide,
                    }}
                >
                    {badge}
                </span>
            )}
        </button>
    );
}
